<template>
  <main class="ErrorPage">
    <div
      class="ErrorPage__icon"
      :class="{ 'ErrorPage__icon--internal': isInternalServerError }"></div>
    <h1>{{ errorTitle }}</h1>
    <p v-for="(p, index) in errorDescription" :key="index">{{ p }}</p>

    <Button
      v-if="isInternalServerError"
      text="Reload the page"
      color="Grey"
      icon="Reload"
      type="Outline"
      size="Medium"
      @click="reloadOnClick" />

    <a v-else href="/">
      <Button
        text="Go to Home page"
        color="Grey"
        icon="ArrowRight"
        type="Outline"
        size="Medium" />
    </a>
  </main>
  <section v-if="isDebugging" class="ErrorPage__debugInfo">
    <div><label>URL</label>: {{ error.url }}</div>
    <div><label>Status code</label>: {{ error?.statusCode }}</div>
    <div>
      <label>Message</label>: {{ error?.message || error?.statusMessage }}
    </div>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div v-html="error?.stack"></div>
  </section>
</template>

<script lang="ts" setup>
import type { IAppError } from "./types/app/IAppError";
import { Button } from "@frontiers-vue3/public-pages";

interface IErrorProps {
  error: IAppError;
}
const props = withDefaults(defineProps<IErrorProps>(), {});

const { domain, favicon16, favicon32, favicon180 } = useRuntimeConfig().public;
const route = useRoute();
const isDebugging = process.env.NODE_ENV === "development";

const errorTitle = computed((): string => {
  if (props.error.statusCode === 404) return "404 - Page not found";
  return "Unhandled error";
});

const errorDescription = computed((): string[] => {
  if (props.error.statusCode === 404)
    return [
      `The page you were looking for doesn't exist on www.${domain}.`,
      "Visit our homepage to browse the full site."
    ];

  return [
    "A problem has occurred and we're working to fix it as fast as we can.",
    "Please try again later."
  ];
});

const isInternalServerError = computed((): boolean => {
  return props.error.statusCode === 500;
});

useHead({
  title: errorTitle.value,
  meta: [{ hid: "robots", name: "robots", content: "noindex, nofollow" }],
  link: [
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: `${favicon16}`
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: `${favicon32}`
    },
    {
      rel: "apple-touch-icon",
      type: "image/png",
      sizes: "180x180",
      href: `${favicon180}`
    }
  ]
});

const reloadOnClick = (): void => {
  location.reload();
};

onMounted(() => {
  const aux = {
    path: route.path,
    statusCode: props.error.statusCode,
    statusMessage: props.error.statusMessage
  };
  console.error(aux);
});
</script>

<style lang="scss" scoped>
@import "@frontiers-sass/public-pages/src/config/config";

.ErrorPage {
  width: 100%;
  box-sizing: border-box;
  padding: 156px var(--layout-gap);
  max-width: 592px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media #{$break-md} {
    padding: 120px var(--layout-gap);
  }

  &__icon {
    --error-icon-size: 88px;
    width: var(--error-icon-size);
    height: var(--error-icon-size);
    position: relative;

    @media #{$break-md} {
      --error-icon-size: 64px;
    }

    &:after {
      content: "";
      width: var(--error-icon-size);
      height: var(--error-icon-size);
      background: url(data:image/svg+xml;base64,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);
      position: absolute;
      background-size: var(--error-icon-size) var(--error-icon-size);
      top: 0;
      left: 0;
    }

    &--internal {
      &:after {
        background: url(data:image/svg+xml;base64,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);
      }
    }
  }

  h1 {
    @include f-40-500;
    margin: 32px 0;

    @media #{$break-laptop-sm} {
      @include f-32;
    }

    @media #{$break-md} {
      margin: 24px 0;
    }

    @media #{$break-xs} {
      @include f-24;
    }
  }

  p {
    @include f-16-300;
  }

  & > .Button,
  & > a {
    margin-top: 24px;
    display: block;

    @media #{$break-xs} {
      width: 100%;
    }
  }

  .Button {
    color: $textPrimary;

    &::after {
      color: $textPrimary;
      top: 10px;
    }

    @media #{$break-xs} {
      width: 100%;
      padding: 8px 24px;

      &:after {
        position: relative;
        right: 0;
        top: 0;
        margin-left: 8px;
      }
    }
  }

  &__debugInfo {
    margin: 24px 8px;
    padding: 16px;
    background: $white;
    border: 2px solid $grey80;
    border-radius: 8px;
    overflow-x: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-size: 12px;
    line-height: 1.5;

    pre,
    div {
      font-size: 1.4em;
      text-align: left;
    }

    label {
      font-size: 1.1em;
      font-weight: 500;
    }
  }

  * {
    text-align: center;
  }
}
</style>
